import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Button,
} from "@mui/material";
import {
  School as StudentIcon,
  Assignment as ApplicationIcon,
  FormatListBulleted as MyRequestIcon,
  Route as RouteIcon,
  ExitToApp as LogoutIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import TransportApplication from "./Tabs/TransportForm/index";
import RouteManagementTabs from "./Tabs/RouteManagementTabs";
import StudentDetailsTab from "./Tabs/StudentDetailsTab";
import StudentManagement from "./Tabs/StudentManagement";
import { jwtDecode } from "jwt-decode";

const Index = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [activeSection, setActiveSection] = useState("student-details");
  const [userRole, setUserRole] = useState(10);

  const token = localStorage.getItem("token");
  const userEmail = localStorage.getItem("userEmail") || "";
  const hasStudents = localStorage.getItem("hasStudents");

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get("tab");

    if (tabParam === "student-details") {
      setActiveSection("student-details");
    } else if (tabParam === "transport") {
      setActiveSection("transport");
    } else if (tabParam === "my-request") {
      setActiveSection("my-request");
    } else if (hasStudents === "false") {
      setActiveSection("student-details");
    } else {
      setActiveSection("transport");
    }
  }, [location.search, hasStudents]);

  useEffect(() => {
    if (token) {
      try {
        const decodedToken = jwtDecode(token);
        setUserRole(decodedToken.role);
      } catch (err) {
        console.error("Error decoding token:", err);
      }
    }
  }, [token]);

  const handleMenuItemClick = (section) => {
    setActiveSection(section);
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/admin/login");
  };

  return (
    <Box sx={{ width: "100%", px: { xs: 1, sm: 2 }, py: 2 }}>
      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, gap: { xs: 1.5, md: 2 }, minHeight: "80vh", width: "100%" }}>
        {/* Compact, Fixed Left Sidebar Menu moved close to left edge */}
        <Paper
          elevation={2}
          sx={{
            width: { xs: "100%", md: 210 },
            flexShrink: 0,
            borderRadius: 0,
            p: { xs: 1, sm: 1.5 },
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            backgroundColor: "#ffffff",
            border: "1px solid #e2e8f0",
          }}
        >
          <Box>
            <Box sx={{ px: 1, py: 1, mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary">
                Parent Portal
              </Typography>
              {userEmail && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    wordBreak: "break-all",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mt: 0.5,
                    fontSize: "0.75rem",
                  }}
                >
                  <PersonIcon fontSize="inherit" color="action" /> {userEmail}
                </Typography>
              )}
            </Box>

            <Divider sx={{ mb: 1.5 }} />

            <List
              disablePadding
              sx={{
                display: "flex",
                flexDirection: { xs: "row", md: "column" },
                width: "100%",
                gap: 0.5,
                py: { xs: 0.5, md: 0 },
              }}
            >
              {/* Student Details Menu Item */}
              <ListItem
                button
                onClick={() => handleMenuItemClick("student-details")}
                selected={activeSection === "student-details"}
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: 2,
                  py: { xs: 1, sm: 1.2 },
                  px: { xs: 0.5, sm: 1.5 },
                  textAlign: "center",
                  "&.Mui-selected": {
                    backgroundColor: "#e3f2fd",
                    color: "#1976d2",
                    "& .MuiListItemIcon-root": { color: "#1976d2" },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: { xs: 0, sm: 32, md: 36 }, justifyContent: "center", mr: { xs: 0, sm: 0.5, md: 1 }, mb: { xs: 0.3, sm: 0 } }}>
                  <StudentIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Student Details"
                  primaryTypographyProps={{
                    fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
                    fontWeight: activeSection === "student-details" ? 600 : 400,
                    textAlign: "center",
                    lineHeight: 1.2,
                  }}
                />
              </ListItem>

              {/* Application Menu Item */}
              <ListItem
                button
                onClick={() => handleMenuItemClick("transport")}
                selected={activeSection === "transport"}
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: 2,
                  py: { xs: 1, sm: 1.2 },
                  px: { xs: 0.5, sm: 1.5 },
                  textAlign: "center",
                  "&.Mui-selected": {
                    backgroundColor: "#e3f2fd",
                    color: "#1976d2",
                    "& .MuiListItemIcon-root": { color: "#1976d2" },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: { xs: 0, sm: 32, md: 36 }, justifyContent: "center", mr: { xs: 0, sm: 0.5, md: 1 }, mb: { xs: 0.3, sm: 0 } }}>
                  <ApplicationIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="Application"
                  primaryTypographyProps={{
                    fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
                    fontWeight: activeSection === "transport" ? 600 : 400,
                    textAlign: "center",
                    lineHeight: 1.2,
                  }}
                />
              </ListItem>

              {/* My Request Menu Item */}
              <ListItem
                button
                onClick={() => handleMenuItemClick("my-request")}
                selected={activeSection === "my-request"}
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: "center",
                  alignItems: "center",
                  borderRadius: 2,
                  py: { xs: 1, sm: 1.2 },
                  px: { xs: 0.5, sm: 1.5 },
                  textAlign: "center",
                  "&.Mui-selected": {
                    backgroundColor: "#e3f2fd",
                    color: "#1976d2",
                    "& .MuiListItemIcon-root": { color: "#1976d2" },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: { xs: 0, sm: 32, md: 36 }, justifyContent: "center", mr: { xs: 0, sm: 0.5, md: 1 }, mb: { xs: 0.3, sm: 0 } }}>
                  <MyRequestIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary="My Request"
                  primaryTypographyProps={{
                    fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
                    fontWeight: activeSection === "my-request" ? 600 : 400,
                    textAlign: "center",
                    lineHeight: 1.2,
                  }}
                />
              </ListItem>

              {/* Route Management (If Admin) */}
              {userRole < 2 && (
                <ListItem
                  button
                  onClick={() => handleMenuItemClick("route")}
                  selected={activeSection === "route"}
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "center",
                    alignItems: "center",
                    borderRadius: 2,
                    py: { xs: 1, sm: 1.2 },
                    px: { xs: 0.5, sm: 1.5 },
                    textAlign: "center",
                    "&.Mui-selected": {
                      backgroundColor: "#e3f2fd",
                      color: "#1976d2",
                      "& .MuiListItemIcon-root": { color: "#1976d2" },
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: { xs: 0, sm: 32, md: 36 }, justifyContent: "center", mr: { xs: 0, sm: 0.5, md: 1 }, mb: { xs: 0.3, sm: 0 } }}>
                    <RouteIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Route Manage"
                    primaryTypographyProps={{
                      fontSize: { xs: "0.75rem", sm: "0.85rem", md: "0.9rem" },
                      fontWeight: activeSection === "route" ? 600 : 400,
                      textAlign: "center",
                      lineHeight: 1.2,
                    }}
                  />
                </ListItem>
              )}
            </List>
          </Box>
        </Paper>

        {/* Maximized Right Main Content Panel */}
        <Paper
          elevation={1}
          sx={{
            flexGrow: 1,
            width: "100%",
            borderRadius: 0,
            p: { xs: 1.5, sm: 2.5 },
            backgroundColor: "#ffffff",
            border: "1px solid #e2e8f0",
            minHeight: "80vh",
            overflowX: "auto",
          }}
        >
          {activeSection === "student-details" && (
            <StudentDetailsTab
              parentEmail={userEmail}
              onStudentAdded={() => {
                // Optional callback
              }}
            />
          )}

          {activeSection === "transport" && <TransportApplication />}

          {activeSection === "my-request" && <StudentManagement />}

          {activeSection === "route" && <RouteManagementTabs />}
        </Paper>
      </Box>
    </Box>
  );
};

export default Index;
